<script setup lang="ts">
    import type { HandleMenuKeypress } from '~/components/atm/DesktopMenuItem.vue';
    import type { CallToAction, MenuItem } from '~/graphql/generated';

    declare interface HeaderDesktopNavProps {
        menu: {
            items: MenuItem[];
            cta?: CallToAction | null;
            ctavisibility?: string | null;
        };
        menuLevel: number;
        submenuId: string;
        handleMenuKeypress: HandleMenuKeypress;
        handleMouseover?: (menuItemId: string) => void;
        handleDetails: (menuItem: MenuItem) => void;
        activeMenuId?: string;
    }

    const props = defineProps<HeaderDesktopNavProps>();
    const nuxtApp = useNuxtApp();

    const menuListProps = computed(() => {
        return props.menu.items.some((item) => item.items?.length)
            ? {
                  role: 'menubar',
                  'aria-orientation': 'vertical',
              }
            : ({} as Record<string, any>);
    });

    let didTrackMouseInteraction = false;
    const trackMouseInteraction = () => {
        if (didTrackMouseInteraction) return;
        didTrackMouseInteraction = true;
        nuxtApp.$gtm.pushEvent({
            event: 'click_navigation',
            navigation_menu_level: props.menuLevel - 1,
            navigation_menu_id: props.submenuId,
            interaction_type: 'hover_select',
        });
    };
</script>
<template>
    <nav class="mol-header-desktop-nav col-span-4 overflow-y-auto xl:col-span-3">
        <ul
            :id="`${submenuId}-submenu`"
            class="flex list-none flex-col items-stretch py-10"
            @mouseover="trackMouseInteraction"
            v-bind="menuListProps">
            <li
                v-for="item in menu.items"
                class="contents">
                <atm-desktop-menu-item
                    v-if="item"
                    :class="[
                        'flex items-center',
                        {
                            'text-2xl': item.type === 'featured',
                            'text-woom-red': item.items?.length && activeMenuId === item.id,
                        },
                    ]"
                    :handle-menu-keypress="handleMenuKeypress"
                    :item="item"
                    :menu-level="menuLevel"
                    v-bind="getMenuItemProps(item)"
                    @mouseover="() => handleMouseover?.(item.id)"
                    @details="handleDetails" />
            </li>
            <li
                v-if="menu.cta && (!menu.ctavisibility || menu.ctavisibility !== 'mobile')"
                class="self-stretch pb-6 pr-6 pt-6">
                <lazy-atm-button
                    v-bind="ctaToButton(menu.cta)"
                    kind="link"
                    block
                    @keydown.esc.tab="handleMenuKeypress({ event: $event, isOpen: false, level: menuLevel })">
                    <template
                        #icon
                        v-if="menu.cta.icon && isAssetDataIcon(menu.cta.icon)">
                        <component
                            :is="menu.cta.icon.data.icon"
                            class="h-6 w-6" />
                    </template>

                    <renderer-html :content="woomTextFormat(menu.cta.link?.label ?? $t('cta.moreInfo'))" />
                </lazy-atm-button>
            </li>
        </ul>
        <slot></slot>
    </nav>
</template>
